import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { integrityApi, type Escalation } from '@/lib/api/integrity';

/**
 * Cases a trainer decided the TPO should know about.
 *
 * The one place a named student reaches an administrator unprompted, and it
 * only happens because a human who spoke to them decided it was worth it. Every
 * other integrity surface here is verification; this is the to-do list.
 *
 * Rendered only when it has contents. "Nothing to review today" every morning
 * is how a signal teaches an administrator to stop reading it.
 */
export function RaisedToYouCard() {
  const qc = useQueryClient();
  const [acking, setAcking] = useState<Escalation | null>(null);

  const { data } = useQuery({
    queryKey: ['org', 'escalations'],
    queryFn: async () => (await integrityApi.listEscalations()).data,
  });

  const rows = data ?? [];
  if (rows.length === 0) return null;

  return (
    <>
      <Card className="shadow-none">
        <div className="flex items-center gap-2 border-b px-4 py-3">
          <TriangleAlert className="h-4 w-4 text-sky-700" aria-hidden="true" />
          <span className="text-sm font-semibold">Raised to you</span>
          <Badge className="bg-sky-700 px-2 py-px text-[11px] tabular-nums hover:bg-sky-700">
            {rows.length}
          </Badge>
          <span className="ml-auto text-[11px] text-muted-foreground">by your trainers</span>
        </div>

        <ul className="divide-y">
          {rows.map((e) => (
            <EscalationRow key={e.id} escalation={e} onAcknowledge={() => setAcking(e)} />
          ))}
        </ul>
      </Card>

      <AcknowledgeDialog
        escalation={acking}
        onClose={() => setAcking(null)}
        onDone={() => {
          void qc.invalidateQueries({ queryKey: ['org', 'escalations'] });
          setAcking(null);
        }}
      />
    </>
  );
}

function EscalationRow({
  escalation: e,
  onAcknowledge,
}: {
  escalation: Escalation;
  onAcknowledge: () => void;
}) {
  return (
    <li className="px-4 py-3.5">
      <div className="mb-1 flex flex-wrap items-baseline gap-2">
        <span className="text-sm font-semibold">{e.student_name}</span>
        <span className="text-xs text-muted-foreground">{e.batch_name}</span>
      </div>

      {/*
        The trainer's sentence is the headline and the machine's label is the
        footnote. Reverse the two and the row becomes a charge sheet with a
        corroborating quote; as written it is a colleague reporting what they
        found. That ordering is the whole ethical load of this screen.
      */}
      <blockquote className="border-l-2 border-sky-200 pl-2.5 text-sm leading-relaxed">
        “{e.note}”
        <span className="mt-0.5 block text-[11px] not-italic text-muted-foreground">
          — {e.raised_by} · {formatDay(e.raised_at)}
        </span>
      </blockquote>

      <p className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
        {e.reason && (
          <span className="rounded border border-sky-200 bg-sky-50 px-1.5 py-px font-semibold text-sky-800">
            {humaniseReason(e.reason)}
          </span>
        )}
        {e.assessment_title}
      </p>

      <div className="mt-2.5 flex flex-wrap gap-2">
        <Button size="sm" className="h-7 px-3 text-xs" onClick={onAcknowledge}>
          Acknowledge
        </Button>
        <Button asChild variant="outline" size="sm" className="h-7 px-3 text-xs">
          <Link
            to="/org/batches/$batchId/students/$userId"
            params={{ batchId: e.batch_id, userId: e.student_id }}
          >
            View student
          </Link>
        </Button>
      </div>
    </li>
  );
}

/**
 * Acknowledging is recording that you have seen it, not closing it.
 *
 * The wording matters and is repeated in the dialog: an administrator who
 * believes they have settled the matter will stop expecting the trainer to,
 * and the concern quietly falls between them.
 */
function AcknowledgeDialog({
  escalation,
  onClose,
  onDone,
}: {
  escalation: Escalation | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [note, setNote] = useState('');

  const ack = useMutation({
    mutationFn: () => integrityApi.acknowledge(escalation!.id, note.trim() || undefined),
    onSuccess: () => {
      toast.success(`Acknowledged — ${escalation?.student_name}`);
      setNote('');
      onDone();
    },
    onError: () => toast.error('That could not be saved.'),
  });

  return (
    <Dialog open={!!escalation} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Acknowledge — {escalation?.student_name}</DialogTitle>
          <DialogDescription>
            This records that you have seen it. It does not close the concern — that stays with the
            trainer who raised it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="ack-note" className="text-xs">
            What you did about it (optional)
          </Label>
          <Textarea
            id="ack-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder="Called them in. Redoing the assignment under supervision."
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => ack.mutate()} disabled={ack.isPending}>
            {ack.isPending ? 'Saving…' : 'Acknowledge'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The machine's vocabulary, in words a TPO reads once a month.
 *
 * Falls back to the raw value rather than hiding an unknown one: a reason we
 * forgot to translate should look untranslated, not disappear.
 */
function humaniseReason(reason: string): string {
  const map: Record<string, string> = {
    arrived_whole: 'Arrived whole',
    one_commit: 'Arrived in one commit',
    matches: 'Shares code with classmates',
    stuck: 'Stuck',
    behind: 'Behind the batch',
    inactive: 'Inactive',
    never_started: 'Never started',
    pasted: 'Arrived whole',
    assisted: 'Part pasted',
  };
  return map[reason] ?? reason;
}

function formatDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { day: '2-digit', month: 'short' });
}
